import {Button, Container, Stack, Text, TextInput, Title} from '@mantine/core'
import {useForm} from '@mantine/form'
import {createFileRoute} from '@tanstack/react-router'
import {useState} from 'react'

import {PageEnvironmentBanner} from '../app-shell/environment-banner'
import {login} from '../auth/auth-api'
import {AuthBrandHeader} from '../auth/auth-brand-header'
import type {LoginResponse} from '../auth/schemas'

export const Route = createFileRoute('/login')({
    component: LoginRoute,
})

type LoginOutcome =
    | {
          kind: 'email_sent'
          email: string
          fromEmail: string
      }
    | {
          kind: 'not_registered'
          email: string
      }

function isValidLoginEmail(value: string): boolean {
    const email = value.trim()
    return /^\S+@(\S+\.\S+|localhost)$/.test(email)
}

function loginOutcomeFromResponse(email: string, response: LoginResponse): LoginOutcome {
    if (response.status === 'not_registered') {
        return {kind: 'not_registered', email}
    }

    return {
        kind: 'email_sent',
        email,
        fromEmail: response.from_email,
    }
}

function LoginRoute() {
    return (
        <>
            <PageEnvironmentBanner />
            <LoginPage />
        </>
    )
}

function LoginPage() {
    const [outcome, setOutcome] = useState<LoginOutcome | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const form = useForm({
        initialValues: {email: ''},
        validate: {
            email: (value) => (isValidLoginEmail(value) ? null : 'Valid email required'),
        },
    })

    const handleSubmit = form.onSubmit(async (values) => {
        setIsSubmitting(true)
        setError(null)
        try {
            const trimmedEmail = values.email.trim()
            const response = await login(trimmedEmail)
            setOutcome(loginOutcomeFromResponse(trimmedEmail, response))
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Something went wrong')
        } finally {
            setIsSubmitting(false)
        }
    })

    if (outcome?.kind === 'email_sent') {
        return (
            <Container size="sm" py="xl">
                <AuthBrandHeader />
                <Stack gap="md">
                    <Title order={1}>Check your email</Title>
                    <Text>
                        If you have an account, you should receive a login link at{' '}
                        <b>{outcome.email}</b>. Click the link to sign in.
                    </Text>
                    <Text>
                        Please add <b>{outcome.fromEmail}</b> to your email contacts to ensure you
                        receive the login link. If you don't see the email within a few minutes,
                        check your spam folder.
                    </Text>
                </Stack>
            </Container>
        )
    }

    if (outcome?.kind === 'not_registered') {
        return (
            <Container size="sm" py="xl">
                <AuthBrandHeader />
                <Stack gap="md">
                    <Title order={1}>You&apos;re not a Depot user yet</Title>
                    <Text>
                        We couldn&apos;t find an active Depot account for <b>{outcome.email}</b>.
                    </Text>
                    <Text>Please contact management to be invited before signing in.</Text>
                </Stack>
            </Container>
        )
    }

    return (
        <Container size="sm" py="xl">
            <AuthBrandHeader />
            <form onSubmit={handleSubmit}>
                <Stack gap="md">
                    <Title order={1}>Log in</Title>
                    <TextInput
                        label="Email"
                        type="text"
                        autoComplete="email"
                        required
                        {...form.getInputProps('email')}
                    />
                    {error && <Text c="red">{error}</Text>}
                    <Button type="submit" loading={isSubmitting}>
                        Send login link
                    </Button>
                </Stack>
            </form>
        </Container>
    )
}
